import type { ArrowData } from '../model/arrow';
import { DIRECTION_VECTORS } from '../model/direction';
import { CELL_SIZE } from './camera';

/**
 * Arrow geometry.
 *
 * An arrow is drawn as a rounded polyline through the centres of its cells, finished with a
 * chevron head. Exit animation reuses the same polyline: the path is extended past the board
 * edge and a window of it slides forward, so the arrow leaves *along its own route* with the
 * tail tracing every corner the head took.
 */

export interface Point {
  x: number;
  y: number;
}

export const STROKE_RATIO = 0.3;
export const HEAD_LENGTH_RATIO = 0.52;
export const HEAD_WIDTH_RATIO = 0.66;
/**
 * Shaft drawn behind a single-cell arrow's head, as a fraction of a cell. The head itself is
 * centred on the cell, so the shaft only has to run back from there.
 */
export const SINGLE_CELL_SHAFT = 0.34;

/**
 * Cell centres from tail to head, in world units.
 *
 * A single-cell arrow gets a short synthetic shaft running back from its cell centre, so it
 * still draws as an arrow with a visible head. Without it such an arrow would render as a
 * featureless dot whose direction the player could not read.
 */
export function arrowPolyline(arrow: ArrowData): Point[] {
  const points = arrow.cells.map((cell) => ({
    x: cell.x * CELL_SIZE + CELL_SIZE / 2,
    y: cell.y * CELL_SIZE + CELL_SIZE / 2,
  }));
  if (points.length > 1) return points;

  const centre = points[0];
  const { dx, dy } = DIRECTION_VECTORS[arrow.direction];
  const shaft = CELL_SIZE * SINGLE_CELL_SHAFT;
  return [{ x: centre.x - dx * shaft, y: centre.y - dy * shaft }, centre];
}

/**
 * The polyline continued beyond the head, far enough to clear any board.
 * Animating along this makes the arrow exit through the edge it points at.
 */
export function exitPolyline(arrow: ArrowData, boardWidth: number, boardHeight: number): Point[] {
  const points = arrowPolyline(arrow);
  const head = points[points.length - 1];
  const { dx, dy } = DIRECTION_VECTORS[arrow.direction];
  const runway = (Math.max(boardWidth, boardHeight) + 2) * CELL_SIZE;
  points.push({ x: head.x + dx * runway, y: head.y + dy * runway });
  return points;
}

export function polylineLength(points: readonly Point[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += Math.abs(points[i].x - points[i - 1].x) + Math.abs(points[i].y - points[i - 1].y);
  }
  return total;
}

/**
 * The section of a polyline between two arc-length positions.
 *
 * Segments are axis-aligned, so Manhattan distance is the true arc length and no square
 * roots are needed. Returns an empty array when the window falls outside the path.
 */
export function subPolyline(points: readonly Point[], from: number, to: number): Point[] {
  if (to <= from || points.length < 2) return [];
  const result: Point[] = [];
  let travelled = 0;

  for (let i = 1; i < points.length; i += 1) {
    const start = points[i - 1];
    const end = points[i];
    const length = Math.abs(end.x - start.x) + Math.abs(end.y - start.y);
    if (length === 0) continue;

    const segmentStart = travelled;
    const segmentEnd = travelled + length;
    travelled = segmentEnd;

    if (segmentEnd <= from || segmentStart >= to) continue;

    const enter = Math.max(from, segmentStart);
    const leave = Math.min(to, segmentEnd);
    const enterRatio = (enter - segmentStart) / length;
    const leaveRatio = (leave - segmentStart) / length;

    const enterPoint = {
      x: start.x + (end.x - start.x) * enterRatio,
      y: start.y + (end.y - start.y) * enterRatio,
    };
    const leavePoint = {
      x: start.x + (end.x - start.x) * leaveRatio,
      y: start.y + (end.y - start.y) * leaveRatio,
    };

    if (result.length === 0) result.push(enterPoint);
    result.push(leavePoint);
  }
  return result;
}

export interface HeadGeometry {
  readonly tip: Point;
  readonly left: Point;
  readonly right: Point;
  /** Where the shaft should stop so it does not poke through the head. */
  readonly shaftEnd: Point;
}

/**
 * Chevron head for the final segment of a polyline.
 *
 * The head is centred on the end of the polyline — the head cell's centre — rather than
 * having its tip there. Anchoring the tip put the whole triangle *behind* the cell centre,
 * which left the head cell reading as empty next to the dot grid; centring it makes the cell
 * plainly occupied. The triangle still fits well inside its cell: it reaches half a head
 * length past the centre, about a quarter of a cell.
 */
export function headGeometry(points: readonly Point[], cellSize = CELL_SIZE): HeadGeometry | null {
  if (points.length < 2) return null;
  const end = points[points.length - 1];
  const previous = points[points.length - 2];

  const dx = end.x - previous.x;
  const dy = end.y - previous.y;
  const length = Math.abs(dx) + Math.abs(dy);
  if (length === 0) return null;

  const ux = dx / length;
  const uy = dy / length;
  const headLength = Math.min(cellSize * HEAD_LENGTH_RATIO, length);
  const halfWidth = (cellSize * HEAD_WIDTH_RATIO) / 2;

  // Slide the whole triangle forward so its axis is centred on the polyline's end point.
  const overhang = headLength / 2;
  const tip = { x: end.x + ux * overhang, y: end.y + uy * overhang };
  const baseX = tip.x - ux * headLength;
  const baseY = tip.y - uy * headLength;

  return {
    tip,
    left: { x: baseX - uy * halfWidth, y: baseY + ux * halfWidth },
    right: { x: baseX + uy * halfWidth, y: baseY - ux * halfWidth },
    // Overlap the head slightly so no seam shows between shaft and chevron.
    shaftEnd: { x: tip.x - ux * headLength * 0.55, y: tip.y - uy * headLength * 0.55 },
  };
}
